import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Transfer.css";

const API = "https://okna.co.tz/mrms/Payment.php";

export default function Transfer() {
  const token = localStorage.getItem("token");
  
  // State management
  const [balances, setBalances] = useState({});
  const [transfers, setTransfers] = useState([]);
  const [dailyBalances, setDailyBalances] = useState([]);
  const [activeTab, setActiveTab] = useState("balances");
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState("");
  
  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    from_account: "CASH",
    to_account: "M-PESA",
    amount: "",
    reason: ""
  });
  
  // Manual cash to safe transfer state
  const [safeTransfer, setSafeTransfer] = useState({
    amount: "",
    reason: ""
  });
  
  // Account list - MATCH EXACT DATABASE NAMES
  const accounts = [
    { code: "CASH", name: "💵 Cash Register", color: "#4caf50" },
    { code: "M-PESA", name: "📱 M-PESA", color: "#ff9800" },
    { code: "MIX_BY_YAS", name: "🔄 Mix By Yas", color: "#2196f3" },
    { code: "SELECOM", name: "💳 Selecom", color: "#9c27b0" },
    { code: "CRDB_BANK", name: "🏦 CRDB Bank", color: "#f44336" },
    { code: "OKNA_SAFE", name: "🔒 OKNA Safe", color: "#795548" }
  ];
  
  // Helper function for authenticated fetch
  const authFetch = async (url, options = {}) => {
    if (!token) {
      throw new Error("No token found");
    }
    
    const separator = url.includes('?') ? '&' : '?';
    const urlWithToken = `${url}${separator}token=${token}`;
    
    const response = await fetch(urlWithToken, {
      ...options,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers
      }
    });
    
    if (response.status === 401) {
      Swal.fire("Session Expired", "Please login again", "warning");
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      window.location.href = "/";
      throw new Error("Unauthorized");
    }
    
    return response;
  };
  
  // Fetch all data
  useEffect(() => {
    if (!token) {
      Swal.fire("Session expired", "Please login again", "warning");
      return;
    }
    fetchBalances();
    fetchTransferHistory();
    fetchDailyBalance();
  }, [selectedDate, token]);
  
  const fetchBalances = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}?action=balances`);
      const data = await res.json();
      
      let balanceMap = {};
      
      if (Array.isArray(data)) {
        data.forEach(item => {
          balanceMap[item.account_name] = parseFloat(item.current_balance) || 0;
        });
      }
      
      console.log("Fetched balances:", balanceMap); // Debug log
      setBalances(balanceMap);
      
    } catch (e) {
      console.error("Error fetching balances", e);
      if (e.message !== "Unauthorized") {
        Swal.fire("Error", "Failed to fetch balances. Please check your connection.", "error");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // FIXED: Sync all payments at once using bulk sync endpoint
  const syncExistingPayments = async () => {
    try {
      setSyncing(true);
      setSyncProgress("Resetting and syncing all payments...");
      
      const res = await authFetch(`${API}?action=sync_all_payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });
      
      const result = await res.json();
      
      if (result.success) {
        setSyncProgress("Sync completed! Refreshing balances...");
        
        // Force refresh balances with a small delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh all data
        await fetchBalances();
        await fetchTransferHistory();
        await fetchDailyBalance();
        
        // Force a second refresh to be sure
        await new Promise(resolve => setTimeout(resolve, 300));
        await fetchBalances();
        
        let message = `✅ Successfully synced ${result.synced_count} payments!\n\n`;
        if (result.account_totals) {
          message += `📊 Account Balances:\n`;
          message += `💰 CASH: ${formatMoney(result.account_totals.CASH || 0)}\n`;
          message += `📱 M-PESA: ${formatMoney(result.account_totals["M-PESA"] || 0)}\n`;
          message += `🔄 MIX BY YAS: ${formatMoney(result.account_totals["MIX_BY_YAS"] || 0)}\n`;
          message += `💳 SELECOM: ${formatMoney(result.account_totals.SELECOM || 0)}\n`;
          message += `🏦 CRDB BANK: ${formatMoney(result.account_totals["CRDB_BANK"] || 0)}`;
        }
        
        Swal.fire({
          title: "Sync Complete!",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#667eea"
        }).then(() => {
          // Refresh one more time after user clicks OK
          fetchBalances();
        });
      } else {
        Swal.fire("Error", result.error || "Sync failed", "error");
      }
      
    } catch (e) {
      console.error("Error syncing payments", e);
      Swal.fire("Error", "Failed to sync payments: " + e.message, "error");
    } finally {
      setSyncing(false);
      setSyncProgress("");
    }
  };
  
  const fetchTransferHistory = async () => {
    try {
      const res = await authFetch(`${API}?action=transfers&limit=50`);
      const data = await res.json();
      
      if (Array.isArray(data)) {
        setTransfers(data);
      } else {
        setTransfers([]);
      }
    } catch (e) {
      console.error("Error fetching transfers", e);
      setTransfers([]);
    }
  };
  
  const fetchDailyBalance = async () => {
    try {
      const res = await authFetch(`${API}?action=daily_balance&date=${selectedDate}`);
      const data = await res.json();
      setDailyBalances(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching daily balances", e);
    }
  };
  
  // Submit transfer between accounts
  const submitTransfer = async () => {
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      Swal.fire("Error", "Please enter a valid amount", "error");
      return;
    }
    
    if (transferForm.from_account === transferForm.to_account) {
      Swal.fire("Error", "Source and destination accounts cannot be the same", "error");
      return;
    }
    
    const currentBalance = balances[transferForm.from_account] || 0;
    if (parseFloat(transferForm.amount) > currentBalance) {
      Swal.fire("Error", `Insufficient balance in ${getAccountDisplayName(transferForm.from_account)}. Available: ${formatMoney(currentBalance)}`, "error");
      return;
    }
    
    try {
      setLoading(true);
      
      const transferData = {
        from_account: transferForm.from_account,
        to_account: transferForm.to_account,
        amount: parseFloat(transferForm.amount),
        reason: transferForm.reason || "Manual transfer"
      };
      
      const res = await authFetch(`${API}?action=transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(transferData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        Swal.fire("Success", `Transfer of ${formatMoney(transferForm.amount)} completed successfully!`, "success");
        setTransferForm({
          ...transferForm,
          amount: "",
          reason: ""
        });
        await fetchBalances();
        await fetchTransferHistory();
        await fetchDailyBalance();
      } else {
        Swal.fire("Error", data.error || "Transfer failed", "error");
      }
    } catch (e) {
      console.error("Transfer error:", e);
      if (e.message !== "Unauthorized") {
        Swal.fire("Error", "Server error. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Transfer cash to safe
  const submitSafeTransfer = async () => {
    if (!safeTransfer.amount || parseFloat(safeTransfer.amount) <= 0) {
      Swal.fire("Error", "Please enter a valid amount", "error");
      return;
    }
    
    const cashBalance = balances["CASH"] || 0;
    if (parseFloat(safeTransfer.amount) > cashBalance) {
      Swal.fire("Error", `Insufficient cash balance. Available: ${formatMoney(cashBalance)}`, "error");
      return;
    }
    
    try {
      setLoading(true);
      
      const transferData = {
        from_account: "CASH",
        to_account: "OKNA_SAFE",
        amount: parseFloat(safeTransfer.amount),
        reason: safeTransfer.reason || "Cash deposit to safe"
      };
      
      const res = await authFetch(`${API}?action=transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(transferData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        Swal.fire("Success", `${formatMoney(safeTransfer.amount)} transferred to OKNA Safe!`, "success");
        setSafeTransfer({ amount: "", reason: "" });
        await fetchBalances();
        await fetchTransferHistory();
        await fetchDailyBalance();
      } else {
        Swal.fire("Error", data.error || "Transfer failed", "error");
      }
    } catch (e) {
      console.error("Safe transfer error:", e);
      if (e.message !== "Unauthorized") {
        Swal.fire("Error", "Server error. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Format money
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('sw-TZ', { style: 'currency', currency: 'TZS' }).format(amount || 0);
  };
  
  // Get total balance
  const getTotalBalance = () => {
    return Object.values(balances).reduce((sum, val) => sum + (val || 0), 0);
  };
  
  // Get account color
  const getAccountColor = (code) => {
    const account = accounts.find(a => a.code === code);
    return account ? account.color : "#999";
  };
  
  // Get display name for account
  const getAccountDisplayName = (code) => {
    const account = accounts.find(a => a.code === code);
    if (account) {
      if (code === "MIX_BY_YAS") return "MIX BY YAS";
      if (code === "CRDB_BANK") return "CRDB BANK";
      return account.name;
    }
    return code;
  };
  
  return (
    <div className="money-management">
      {/* Header */}
      <div className="header">
        <h1>💰 Money Circulation Manager</h1>
        <div className="total-balance">
          <span>Total Assets:</span>
          <strong>{formatMoney(getTotalBalance())}</strong>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="tabs">
        <button 
          className={activeTab === "balances" ? "active" : ""} 
          onClick={() => setActiveTab("balances")}
        >
          📊 Account Balances
        </button>
        <button 
          className={activeTab === "transfer" ? "active" : ""} 
          onClick={() => setActiveTab("transfer")}
        >
          🔄 Transfer Money
        </button>
        <button 
          className={activeTab === "safe" ? "active" : ""} 
          onClick={() => setActiveTab("safe")}
        >
          🔒 Safe Deposit
        </button>
        <button 
          className={activeTab === "history" ? "active" : ""} 
          onClick={() => setActiveTab("history")}
        >
          📜 Transaction History
        </button>
        <button 
          className={activeTab === "daily" ? "active" : ""} 
          onClick={() => setActiveTab("daily")}
        >
          📅 Daily Report
        </button>
      </div>
      
      {/* Loading Overlay */}
      {(loading || syncing) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>{syncProgress || (syncing ? "Syncing payments..." : "Processing...")}</p>
          </div>
        </div>
      )}
      
      {/* Tab Content: Account Balances */}
      {activeTab === "balances" && (
        <>
          <div className="sync-button-container">
            <button 
              className="btn-sync" 
              onClick={syncExistingPayments} 
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "🔄 Sync Existing Payments"}
            </button>
            <p className="sync-info">
              Click to calculate balances from all existing paid orders.
              This will update CASH, M-PESA, MIX BY YAS, SELECOM, and CRDB BANK accounts.
            </p>
          </div>
          
          <div className="balances-grid">
            {accounts.map(account => {
              const balance = balances[account.code] || 0;
              return (
                <div className="balance-card" key={account.code} style={{ borderTopColor: account.color }}>
                  <div className="card-header">
                    <span className="account-icon">{account.name.split(' ')[0]}</span>
                    <span className="account-name">{account.name}</span>
                  </div>
                  <div className="card-balance" style={{ color: account.color }}>
                    {formatMoney(balance)}
                  </div>
                  <div className="card-code">
                    {account.code === "MIX_BY_YAS" ? "MIX BY YAS" : 
                     account.code === "CRDB_BANK" ? "CRDB BANK" : 
                     account.code}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      
      {/* Tab Content: Transfer Money */}
      {activeTab === "transfer" && (
        <div className="transfer-section">
          <div className="form-card">
            <h2>Transfer Between Accounts</h2>
            
            <div className="form-group">
              <label>From Account</label>
              <select 
                value={transferForm.from_account} 
                onChange={(e) => setTransferForm({...transferForm, from_account: e.target.value})}
              >
                {accounts.map(acc => (
                  <option key={acc.code} value={acc.code}>
                    {getAccountDisplayName(acc.code)} (Balance: {formatMoney(balances[acc.code] || 0)})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>To Account</label>
              <select 
                value={transferForm.to_account} 
                onChange={(e) => setTransferForm({...transferForm, to_account: e.target.value})}
              >
                {accounts.map(acc => (
                  <option key={acc.code} value={acc.code}>
                    {getAccountDisplayName(acc.code)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Amount (TZS)</label>
              <input
                type="number"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                placeholder="Enter amount"
                min="0"
                step="1000"
              />
            </div>
            
            <div className="form-group">
              <label>Reason (Optional)</label>
              <input
                type="text"
                value={transferForm.reason}
                onChange={(e) => setTransferForm({...transferForm, reason: e.target.value})}
                placeholder="e.g., Daily cash pickup, Bank deposit"
              />
            </div>
            
            <button className="btn-primary" onClick={submitTransfer} disabled={loading}>
              🚀 Execute Transfer
            </button>
          </div>
          
          <div className="info-card">
            <h3>ℹ️ Transfer Rules</h3>
            <ul>
              <li>✓ All transfers are recorded with timestamp</li>
              <li>✓ Transfers cannot exceed available balance</li>
              <li>✓ Both accounts must be active</li>
              <li>✓ All movements are auditable</li>
              <li>✓ Use for cash mgmt, bank deposits, etc.</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Tab Content: Safe Deposit */}
      {activeTab === "safe" && (
        <div className="transfer-section">
          <div className="form-card safe-card">
            <h2>🔒 Deposit to OKNA Safe</h2>
            <p className="safe-info">Transfer cash from register to safe for security</p>
            
            <div className="cash-info">
              <span>Current Cash Balance:</span>
              <strong style={{ color: "#4caf50" }}>{formatMoney(balances["CASH"] || 0)}</strong>
            </div>
            
            <div className="form-group">
              <label>Amount to Deposit (TZS)</label>
              <input
                type="number"
                value={safeTransfer.amount}
                onChange={(e) => setSafeTransfer({...safeTransfer, amount: e.target.value})}
                placeholder="Enter amount to move to safe"
                min="0"
                step="1000"
              />
            </div>
            
            <div className="form-group">
              <label>Reason / Notes</label>
              <input
                type="text"
                value={safeTransfer.reason}
                onChange={(e) => setSafeTransfer({...safeTransfer, reason: e.target.value})}
                placeholder="e.g., Daily cash drop, End of shift"
              />
            </div>
            
            <button className="btn-safe" onClick={submitSafeTransfer} disabled={loading}>
              🔒 Deposit to Safe
            </button>
          </div>
          
          <div className="info-card">
            <h3>💡 Safe Management Tips</h3>
            <ul>
              <li>✓ Regular deposits improve security</li>
              <li>✓ Keep only operational cash in register</li>
              <li>✓ Recommended: Deposit when cash &gt; 500,000 TZS</li>
              <li>✓ Always record reason for audit trail</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Tab Content: Transaction History */}
      {activeTab === "history" && (
        <div className="history-section">
          <div className="history-header">
            <h2>📜 Recent Transactions</h2>
            <button className="btn-refresh" onClick={() => {fetchTransferHistory(); fetchBalances();}}>
              🔄 Refresh
            </button>
          </div>
          
          {!transfers || transfers.length === 0 ? (
            <div className="empty-state">
              <p>No transactions found</p>
              <p style={{ fontSize: "12px", marginTop: "10px" }}>
                Click "Sync Existing Payments" first to populate balances, then make transfers.
              </p>
            </div>
          ) : (
            <div className="transactions-table">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Reference</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Transferred By</th>
                    <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                  {transfers.map(transfer => (
                    <tr key={transfer.id}>
                      <td>{new Date(transfer.transfer_date).toLocaleString()}</td>
                      <td className="reference">{transfer.transfer_reference || '-'}</td>
                      <td>
                        {transfer.from_account ? (
                          <span className="account-badge" style={{ background: getAccountColor(transfer.from_account) }}>
                            {getAccountDisplayName(transfer.from_account)}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        <span className="account-badge" style={{ background: getAccountColor(transfer.to_account) }}>
                          {getAccountDisplayName(transfer.to_account)}
                        </span>
                      </td>
                      <td className="amount">{formatMoney(transfer.amount)}</td>
                      <td>{transfer.reason || "-"}</td>
                      <td>{transfer.transferred_by_name || "System"}</td>
                      <td>
                        <span className={`status-badge ${transfer.status === 'completed' ? 'success' : 'pending'}`}>
                          {transfer.status || 'completed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Tab Content: Daily Report */}
      {activeTab === "daily" && (
        <div className="daily-section">
          <div className="date-picker">
            <label>Select Date:</label>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          
          {dailyBalances.length === 0 ? (
            <div className="empty-state">No data for selected date</div>
          ) : (
            <div className="daily-table">
              <table className="daily-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Opening Balance</th>
                    <th>Total Inflow</th>
                    <th>Total Outflow</th>
                    <th>Closing Balance</th>
                    <th>Net Change</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyBalances.map(day => {
                    const netChange = (day.closing_balance || 0) - (day.opening_balance || 0);
                    return (
                      <tr key={day.account}>
                        <td>
                          <span className="account-badge" style={{ background: getAccountColor(day.account) }}>
                            {getAccountDisplayName(day.account)}
                          </span>
                        </td>
                        <td>{formatMoney(day.opening_balance)}</td>
                        <td className="inflow">{formatMoney(day.total_inflow)}</td>
                        <td className="outflow">{formatMoney(day.total_outflow)}</td>
                        <td><strong>{formatMoney(day.closing_balance)}</strong></td>
                        <td className={netChange >= 0 ? "positive" : "negative"}>
                          {netChange >= 0 ? "+" : ""}{formatMoney(netChange)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}